import {DialogPrimitive as Dialog} from "ch3-ui-lib";
import {PhoneHQButton} from "ch3-ui-lib";
import {Moon, X} from "lucide-react";
import {Fragment, useEffect, useState} from "react";
import {DateTimeFields} from "../DateTimeFields/DateTimeFields.tsx";
import {DND_OPTIONS, DndOption, dndDueDate, dndUntil, nextQuarterHour} from "./dndTime.ts";
import moment from "moment-timezone";

const icon = '!p-0 shrink-0 !text-current';

/** What the dialog silences: everything (avatar menu) or one of the user's lines (Calls column). */
export type DndScope =
    | { kind: 'global' }
    | { kind: 'line'; label: string; number: string };

export interface DndDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    scope: DndScope;
    /** Current state of DND for this scope. `dueDate: null` while enabled means until turned off. */
    enabled: boolean;
    dueDate: Date | null;
    /** Open straight on the options even when DND is on ("Adjust time" in the line menu). */
    adjust?: boolean;
    onTurnOn: (dueDate: Date | null) => Promise<unknown> | void;
    onTurnOff: () => Promise<unknown> | void;
}

const copy = (scope: DndScope) => scope.kind === 'global'
    ? {title: 'Do not disturb', description: 'Silence calls on all your numbers and teams. Callers hear your voicemail or after-hours message.'}
    : {title: scope.label, description: `Silence calls to ${scope.number}. Other numbers keep ringing.`};

/**
 * Do not disturb dialog from the design (Dnd-Global, -GlobalPick, -GlobalCustom, -Active, -Line).
 * While DND is on it first shows "Do not disturb is on" with Adjust time / Turn off.
 */
export const DndDialog = ({open, onOpenChange, scope, enabled, dueDate, adjust, onTurnOn, onTurnOff}: DndDialogProps) => {
    const [adjusting, setAdjusting] = useState(false);
    const [option, setOption] = useState<DndOption | null>(null);
    const [customAt, setCustomAt] = useState(() => nextQuarterHour(new Date()));
    const [busy, setBusy] = useState(false);
    const now = new Date();

    useEffect(() => {
        if (!open) return;
        // Every opening starts clean; "Adjust time" pre-fills the current end time.
        setAdjusting(Boolean(adjust));
        setOption(null);
        setCustomAt(dueDate && dueDate > new Date() ? dueDate : nextQuarterHour(new Date()));
        setBusy(false);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const showActive = enabled && !adjusting;
    const customInPast = option === 'custom' && customAt <= now;
    const canTurnOn = option !== null && !customInPast && !busy;
    const {title, description} = copy(scope);

    const run = async (action: () => Promise<unknown> | void) => {
        setBusy(true);
        try {
            await action();
            onOpenChange(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=open]:animate-in data-[state=open]:fade-in-0"/>
                <Dialog.Content
                    aria-describedby={showActive ? 'dnd-active-desc' : 'dnd-desc'}
                    className={`fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card bg-surface text-ink shadow-overlay outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 ${showActive ? 'w-[min(400px,calc(100%-2rem))]' : 'w-[min(440px,calc(100%-2rem))]'}`}
                >
                    <div className="flex h-14 shrink-0 items-center border-0 border-b border-solid border-line px-5">
                        <Dialog.Title className="m-0 flex-1 truncate text-body-strong">{showActive ? 'Do not disturb' : title}</Dialog.Title>
                        <Dialog.Close aria-label="Close" className="flex size-8 cursor-pointer items-center justify-center rounded-chip text-ink hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus">
                            <X size={18} aria-hidden="true" className={icon}/>
                        </Dialog.Close>
                    </div>

                    {showActive ? (
                        <ActiveBody dueDate={dueDate} now={now}/>
                    ) : (
                        <div className="overflow-y-auto p-5">
                            <p id="dnd-desc" className="m-0 mb-3 text-callout font-normal text-ink-muted">{description}</p>
                            <div role="radiogroup" aria-label="Silence calls" className="flex flex-col overflow-hidden rounded-xl border border-solid border-line">
                                {DND_OPTIONS.map((o, i) => (
                                    <Fragment key={o.value}>
                                        {i > 0 && <div aria-hidden="true" className="mx-4 h-px bg-line"/>}
                                        <OptionRow label={o.label} checked={option === o.value} onSelect={() => setOption(o.value)}/>
                                    </Fragment>
                                ))}
                            </div>
                            {option === 'custom' && (
                                <>
                                    <DateTimeFields value={customAt} onChange={setCustomAt} now={now}/>
                                    {customInPast && <p role="alert" className="m-0 mt-2 text-footnote text-danger-ink">Choose a time in the future.</p>}
                                </>
                            )}
                        </div>
                    )}

                    <div className="flex shrink-0 items-center justify-end gap-3 border-0 border-t border-solid border-line px-5 py-4">
                        {showActive ? (
                            <>
                                <PhoneHQButton variant="secondary" className="px-5" onClick={() => setAdjusting(true)}>Adjust time</PhoneHQButton>
                                <PhoneHQButton disabled={busy} className="px-5" onClick={() => run(onTurnOff)}>Turn off</PhoneHQButton>
                            </>
                        ) : (
                            <>
                                <PhoneHQButton variant="secondary" className="px-5" onClick={() => onOpenChange(false)}>Cancel</PhoneHQButton>
                                <PhoneHQButton
                                    disabled={!canTurnOn}
                                    className="px-5"
                                    onClick={() => option && run(() => onTurnOn(dndDueDate(option, new Date(), customAt)))}
                                >
                                    Turn on
                                </PhoneHQButton>
                            </>
                        )}
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

/** One option in the list: 22px radio, brand fill with a white dot when checked. */
const OptionRow = ({label, checked, onSelect}: { label: string; checked: boolean; onSelect: () => void }) => (
    <button
        type="button"
        role="radio"
        aria-checked={checked}
        onClick={onSelect}
        className="box-border flex min-h-10 w-full cursor-pointer items-center gap-3 px-4 py-2 text-left outline-none hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-focus"
    >
        <span aria-hidden="true" className={`box-border flex size-[22px] flex-none items-center justify-center rounded-full ${checked ? 'bg-brand' : 'border-2 border-solid border-fill-subtle'}`}>
            {checked && <span className="size-2 rounded-full bg-white"/>}
        </span>
        <span className={`flex-1 text-callout ${checked ? 'font-semibold' : ''}`}>{label}</span>
    </button>
);

/** Dnd-Active: moon tile, "Do not disturb is on", until when. */
const ActiveBody = ({dueDate, now}: { dueDate: Date | null; now: Date }) => {
    const today = Boolean(dueDate && moment(dueDate).isSame(now, 'day'));
    return (
        <div className="flex flex-col items-center gap-2 px-5 pb-6 pt-7 text-center">
            <span aria-hidden="true" className="flex size-12 items-center justify-center rounded-[14px] bg-brand text-white">
                <Moon size={24} className={icon}/>
            </span>
            <h3 className="m-0 mt-2 text-body-strong">Do not disturb is on</h3>
            <p id="dnd-active-desc" className="m-0 text-callout font-normal text-ink-muted">
                {dueDate ? (
                    <>Calls are silenced until <b className="font-semibold text-ink">{dndUntil(dueDate, now)}</b>{today ? ' today' : ''}.</>
                ) : (
                    <>Calls are silenced until you turn it off.</>
                )}
            </p>
        </div>
    );
};
